import * as React from 'react';
import { createContext, useContext, useMemo, useState } from 'react';

/**
 * Bridges the two-panel coach column (rendered deep inside SearchFrame) to the
 * page footer (rendered at the search app shell, outside SearchFrame). The coach
 * panel is out-of-flow (position: fixed), so a full-bleed footer would slide
 * under it; the footer reserves `footerCarveRightPx` on its trailing edge so it
 * ends at the panel's leading edge instead — the XDP two-panel approach, but
 * measured because the search coach column lives inside a centered maxWidth="lg"
 * grid rather than docked to the viewport edge.
 */
type CoachColumnLayoutContextType = {
  /** Trailing-edge inset (px) the footer should reserve; 0 disables the carve. */
  footerCarveRightPx: number;
  setFooterCarveRightPx: (px: number) => void;
};

const CoachColumnLayoutContext = createContext<CoachColumnLayoutContextType>({
  footerCarveRightPx: 0,
  setFooterCarveRightPx: () => {},
});

export const CoachColumnLayoutProvider = ({ children }: React.PropsWithChildren) => {
  const [footerCarveRightPx, setFooterCarveRightPx] = useState(0);
  const value = useMemo(() => ({ footerCarveRightPx, setFooterCarveRightPx }), [footerCarveRightPx]);
  return <CoachColumnLayoutContext.Provider value={value}>{children}</CoachColumnLayoutContext.Provider>;
};

export const useCoachColumnLayout = () => useContext(CoachColumnLayoutContext);
